// src/components/CustomerFlow/Steps/PaymentProcessingStep.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faLock } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import api from '../../../services/api';
import { formatCurrency } from '../../../utils/numberFormatter';
import { trackMobilePaymentCardSaved } from '../../../analytics/events';
import './PaymentProcessingStep.css';

const PaymentProcessingStep = ({ 
  flowData, 
  stepStatuses,
  onNext
}) => {
  // Extract IDs from flowData provided by MobileCustomerFlowPage
  const customerId = flowData?.tokenCustomerId;
  const purchaseRequestId = flowData?.tokenPurchaseRequestId;

  const handoffContext = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('temp_handoffContext') || 'null');
    } catch (e) {
      return null;
    }
  }, []);

  const flowCustomer = flowData?.customer || handoffContext?.customer || {};
  const flowBank = flowData?.bank || handoffContext?.bank;
  const flowPurchaseRequest = flowData?.purchase_request || handoffContext?.purchase_request;
  const flowDownpayment = flowData?.downpayment || handoffContext?.downpayment;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCardSaved, setIsCardSaved] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isPaymentWidgetOpen, setIsPaymentWidgetOpen] = useState(false);

  const paymentsInstanceRef = useRef(null);
  const isOpeningWidgetRef = useRef(false);
  const pollingRunRef = useRef(0);
  const completionTriggeredRef = useRef(false);

  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;
  const tokenScope = (() => {
    try {
      return JSON.parse(localStorage.getItem('temp_tokenScope') || '{}');
    } catch (error) {
      return {};
    }
  })();

  const digitsOnly = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).replace(/\D/g, '');
  };

  const expectedHolderId = digitsOnly(
    tokenScope.customer_dni
      || tokenScope.dni
      || tokenScope.document_number
      || tokenScope.customer?.dni
      || localStorage.getItem('temp_tokenDni')
      || flowCustomer?.dni
  ) || null;

  const isWidgetCardHolderValid = useCallback((cardHolderIdentification) => {
    if (!expectedHolderId) return true;

    const cardHolderId = digitsOnly(cardHolderIdentification);
    if (!cardHolderId) return true;

    return cardHolderId === expectedHolderId;
  }, [expectedHolderId]);

  const showCardHolderMismatchAlert = useCallback(async () => {
    setIsPaymentWidgetOpen(false);
    isOpeningWidgetRef.current = false;
    setIsSubmitting(false);

    const paymentsInstance = paymentsInstanceRef.current;
    if (paymentsInstance?.closeWidget) {
      await Promise.resolve(paymentsInstance.closeWidget());
    }

    await new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });

    const customerFirstName = flowCustomer?.firstName || '';
    const customerLastName = flowCustomer?.lastName || '';
    const customerFullName = `${customerFirstName} ${customerLastName}`.trim();

    await Swal.fire({
      html: `
        <div style="text-align: center; padding: 20px 10px;">
          <div style="margin-bottom: 30px; display: flex; justify-content: center; align-items: center;">
            <div style="position: relative; display: inline-block;">
              <i class="bi bi-credit-card" style="color: #6c757d; font-size: 80px; display: inline;"></i>
              <i class="bi bi-x-circle-fill" style="color: #dc3545; font-size: 20px; position: absolute; display: inline; top: 12px; right: -6px; background: white; border-radius: 50%;"></i>
            </div>
          </div>
          <h3 style="font-size: 22px; font-weight: 600; color: #333; margin-bottom: 20px;">
            La tarjeta debe estar a tu nombre
          </h3>
          <p style="font-size: 16px; margin-bottom: 15px; color: #555;">
            ${customerFullName
              ? `La tarjeta ingresada no pertenece a <strong>${customerFullName}</strong>.`
              : 'La tarjeta ingresada no coincide con tu documento.'}
          </p>
          <p style="font-size: 15px; color: #6c757d;">
            Por favor, ingresá una tarjeta de débito que esté a tu nombre.
          </p>
        </div>
      `,
      confirmButtonText: 'Entendido, intentá de nuevo',
      confirmButtonColor: '#435ebe',
      allowOutsideClick: false,
      showCloseButton: false,
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'btn btn-primary',
      },
    });
  }, [flowCustomer?.firstName, flowCustomer?.lastName]);

  const widgetDocument =
    digitsOnly(flowCustomer?.cuil || flowCustomer?.cuilCuit || flowCustomer?.cuit)
    || expectedHolderId
    || null;

  // Auto-activate the step if it is pending
  useEffect(() => {
    const currentStepStatus = stepStatuses['PaymentStep'];
    
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);
      
      onNext({
        step_name: 'PaymentStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  const fetchPaymentStatus = useCallback(async ({ updateState = true } = {}) => {
    const response = await api.get(
      `/customers/${customerId}/purchase-requests/${purchaseRequestId}/payment-status`
    );

    if (updateState) {
      setPaymentStatus(response.data);
    }

    return response.data;
  }, [customerId, purchaseRequestId]);

  useEffect(() => {
    if (!customerId || !purchaseRequestId) {
      return;
    }

    let isActive = true;

    fetchPaymentStatus({ updateState: false })
      .then((status) => {
        if (!isActive) return;
        setPaymentStatus(status);
        setErrorMessage('');
      })
      .catch((error) => {
        if (!isActive) return;
        console.error('Error fetching payment status:', error);
        setErrorMessage('No pudimos cargar el estado del pago. Revisá tu conexión e intentá de nuevo.');
      });

    return () => {
      isActive = false;
    };
  }, [customerId, purchaseRequestId, fetchPaymentStatus, reloadTrigger]);

  const requiresDownpayment = !!paymentStatus?.downpayment?.required || !!flowDownpayment?.required;
  const expectedDownpaymentAmount = Number(
    paymentStatus?.downpayment?.expected_amount
    ?? flowDownpayment?.amount
    ?? 0
  );
  const hasLoadedPaymentStatus = paymentStatus !== null;

  const completePaymentStepFromWidget = useCallback(async (cardData) => {
    const currentRun = pollingRunRef.current;

    if (completionTriggeredRef.current || currentRun !== pollingRunRef.current) {
      return false;
    }

    completionTriggeredRef.current = true;
    setIsCardSaved(true);
    setErrorMessage('');
    setIsPaymentWidgetOpen(false);
    paymentsInstanceRef.current?.closeWidget?.();

    const branchId = localStorage.getItem('temp_tokenBranchId');
    const cardBrand = cardData?.source?.brand || cardData?.source?.brand_name;

    trackMobilePaymentCardSaved({
      commerceId: branchId || 'mobile_flow',
      branchId: branchId || undefined,
      flowId: flowData?.flow_id,
      cardBrand
    });

    const stepData = {
      step_name: 'PaymentStep',
      step_status: 'processed',
      payment_setup_processed: true,
      widget_validated: true,
      requires_downpayment: requiresDownpayment,
      processed_timestamp: new Date().toISOString()
    };

    setTimeout(() => {
      if (currentRun === pollingRunRef.current && completionTriggeredRef.current) {
        onNext(stepData, 'processed');
      }
    }, 1200);

    return true;
  }, [flowData?.flow_id, onNext, requiresDownpayment]);

  const reloadWidget = useCallback(() => {
    pollingRunRef.current += 1;
    completionTriggeredRef.current = false;
    setErrorMessage('');
    setIsCardSaved(false);
    setIsWidgetLoading(true);
    setIsPaymentWidgetOpen(false);
    setIsSubmitting(false);

    if (paymentsInstanceRef.current) {
      paymentsInstanceRef.current.destroy?.();
      paymentsInstanceRef.current.closeWidget?.();
      paymentsInstanceRef.current = null;
    }

    setReloadTrigger((prev) => prev + 1);
  }, []);

  // Load the Cirenio script and prepare the modal widget instance
  useEffect(() => {
    if (!hasLoadedPaymentStatus) {
      return;
    }

    // Verify required IDs before preparing the widget
    if (!customerId || !purchaseRequestId) {
      console.error('Missing required IDs:', { customerId, purchaseRequestId });
      setErrorMessage('Faltan datos para continuar. Pedile ayuda a tu vendedor.');
      return;
    }

    if (!widgetDocument) {
      setIsWidgetLoading(false);
      setErrorMessage('Faltan datos para iniciar el pago. Pedile ayuda a tu vendedor.');
      return;
    }

    let isActive = true;
    let script = null;

    const initializeWidget = () => {
      if (!isActive || !window.CirenioPayments) {
        return;
      }

      if (paymentsInstanceRef.current) {
        setIsWidgetLoading(false);
        return;
      }

      const prKey = flowPurchaseRequest?.uid || purchaseRequestId;
      const paymentsInstance = new window.CirenioPayments(
        publicKey,
        widgetDocument,
        {
          showCloseButton: true,
          externalId: `purchase_request:${prKey}`,
          flowType: requiresDownpayment ? 'payment' : 'tokenization',
          subjectType: 'purchase_request',
          subjectId: String(purchaseRequestId),
          subjectUid: flowPurchaseRequest?.uid || undefined,
          paymentContext: requiresDownpayment ? 'downpayment_debit_card' : 'tokenization_only',
          advancePayment: requiresDownpayment ? {
            amount: expectedDownpaymentAmount,
          } : undefined,
          paymentConcept: requiresDownpayment
            ? (flowPurchaseRequest?.productName || 'Pago inicial de tu compra')
            : undefined,
          paymentDescription: requiresDownpayment ? 'Pago inicial de tu compra' : undefined,
          firstName: flowCustomer?.firstName,
          lastName: flowCustomer?.lastName,
          email: flowCustomer?.email,
          phone: flowCustomer?.phone || undefined,
        }
      );

      paymentsInstance.on('widget_start', () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setIsPaymentWidgetOpen(true);
        }
      });

      paymentsInstance.on('widget_closed', () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setIsPaymentWidgetOpen(false);
        }
      });

      paymentsInstance.on('tokenization_success', async (cardData) => {
        if (!isActive) return;

        const cardHolderId = cardData?.source?.holder?.identification;

        if (!isWidgetCardHolderValid(cardHolderId)) {
          await showCardHolderMismatchAlert();
          reloadWidget();
          return;
        }

        setIsSubmitting(true);
        await completePaymentStepFromWidget(cardData);
        if (isActive) {
          setIsSubmitting(false);
        }
      });

      paymentsInstance.on('tokenization_error', () => {
        if (isActive) {
          setIsWidgetLoading(false);
          setErrorMessage('Hubo un problema con tu tarjeta. Intentá de nuevo.');
        }
      });

      paymentsInstanceRef.current = paymentsInstance;
      setIsWidgetLoading(false);
    };

    if (window.CirenioPayments) {
      initializeWidget();
    } else {
      setIsWidgetLoading(true);
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = initializeWidget;
      script.onerror = () => {
        setIsWidgetLoading(false);
        setErrorMessage('No pudimos abrir la pantalla de pago. Revisá tu conexión e intentá de nuevo.');
      };
      document.body.appendChild(script);
    }

    return () => {
      isActive = false;
      pollingRunRef.current += 1;
      isOpeningWidgetRef.current = false;
      if (paymentsInstanceRef.current) {
        paymentsInstanceRef.current.destroy?.();
        paymentsInstanceRef.current.closeWidget?.();
        paymentsInstanceRef.current = null;
      }
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [
    scriptSrc,
    publicKey,
    customerId,
    purchaseRequestId,
    reloadTrigger,
    widgetDocument,
    expectedHolderId,
    isWidgetCardHolderValid,
    showCardHolderMismatchAlert,
    flowCustomer?.firstName,
    flowCustomer?.lastName,
    flowCustomer?.email,
    flowCustomer?.phone,
    flowPurchaseRequest?.uid,
    flowPurchaseRequest?.productName,
    flowDownpayment?.required,
    flowDownpayment?.amount,
    requiresDownpayment,
    expectedDownpaymentAmount,
    hasLoadedPaymentStatus,
    reloadWidget,
    completePaymentStepFromWidget,
  ]);

  const openPaymentWidget = async () => {
    if (isOpeningWidgetRef.current || isPaymentWidgetOpen || isSubmitting || isCardSaved) {
      return;
    }

    if (!paymentsInstanceRef.current) {
      setErrorMessage('La pantalla de pago todavía se está preparando. Intentá de nuevo en unos segundos.');
      return;
    }

    isOpeningWidgetRef.current = true;
    setErrorMessage('');
    setIsWidgetLoading(true);
    setIsPaymentWidgetOpen(true);

    try {
      const opened = await paymentsInstanceRef.current.openWidget();

      if (opened === false) {
        setIsPaymentWidgetOpen(false);
        setErrorMessage('No pudimos abrir la pantalla de pago. Revisá tu conexión e intentá de nuevo.');
      }
    } catch (error) {
      console.error('Error opening payment widget:', error);
      setIsPaymentWidgetOpen(false);
      setErrorMessage('No pudimos abrir la pantalla de pago. Revisá tu conexión e intentá de nuevo.');
    } finally {
      isOpeningWidgetRef.current = false;
      setIsWidgetLoading(false);
    }
  };

  const renderErrorAlert = () => {
    if (!errorMessage) return null;

    if (
      errorMessage.includes('No pudimos abrir la pantalla de pago')
      || errorMessage.includes('No pudimos cargar el estado de pago')
    ) {
      return (
        <div className="alert alert-warning text-center mt-3" role="alert">
          <FontAwesomeIcon icon={faCreditCard} className="me-2" />
          {errorMessage}{' '}
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ textDecoration: 'underline' }}
            onClick={reloadWidget}
          >
            Reintentar
          </button>.
        </div>
      );
    }

    return (
      <div className="alert alert-warning text-center mt-3" role="alert">
        <FontAwesomeIcon icon={faCreditCard} className="me-2" />
        {errorMessage}
      </div>
    );
  };

  const InfoModal = () => {
    if (!showInfoModal) return null;

    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="modal-content bg-white p-4 rounded-3" style={{
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              {requiresDownpayment ? 'Requisitos para el pago inicial' : 'Requisitos de tu tarjeta'}
            </h6>
            <ul className="list-unstyled">
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe ser una tarjeta de débito
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar a tu nombre
              </li>
              <li className="mb-2">
                <FontAwesomeIcon icon={faLock} className="text-success me-2" />
                Debe estar vinculada a la cuenta donde recibís tus ingresos
              </li>
            </ul>
          </div>
          
          <div className="mb-4">
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              ¿Para qué se utilizará?
            </h6>
            <p className="text-muted mb-2">
              {requiresDownpayment
                ? 'La usamos para cobrar el pago inicial de tu compra. Cuando confirmemos el pago, seguimos con el siguiente paso.'
                : 'La usamos como medio alternativo de cobro y queda vinculada para futuras compras.'}
            </p>
          </div>
          
          <div>
            <h6 className="mb-3">
              <FontAwesomeIcon icon={faLock} className="me-2" />
              Seguridad
            </h6>
            <p className="text-muted mb-0">
              Tus datos son tratados con los más altos estándares de seguridad bancaria y están protegidos por la ley de protección de datos personales
            </p>
          </div>
          
          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowInfoModal(false)}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="payment-processing-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
          <h3>{requiresDownpayment ? 'Hacé el pago inicial' : 'Vinculá tu tarjeta'}</h3>
      </div>

      <div className="step-body">
        <div className="text-center mb-4">
          <p className="lead mb-1">
            {requiresDownpayment ? 'Realizá el pago inicial con tu tarjeta de débito' : 'Vinculá tu tarjeta para los débitos automáticos'}
          </p>
          <p className='lead'>
            {requiresDownpayment
              ? 'Usá una tarjeta de débito a tu nombre. Cuando registremos el pago, te llevamos al siguiente paso.'
              : 'Usá una tarjeta de débito a tu nombre, vinculada a la cuenta donde recibís tus ingresos.'}
          </p>

          <div className="text-center">
            <button
              type="button"
              className="btn btn-link text-primary p-0 text-decoration-none"
              onClick={() => setShowInfoModal(true)}
            >
              <i className="bi bi-info-circle me-1"></i>
              {requiresDownpayment ? '¿Por qué pedimos el pago inicial?' : '¿Para qué necesitamos tu tarjeta?'}
            </button>
          </div>
        </div>

        {!isCardSaved && (
          <div className="payment-widget-launch-card text-center">
            <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-primary mb-3" />
            <h5 className="mb-2">
              {requiresDownpayment ? 'Pagar con tu tarjeta de débito' : 'Cargar datos de la tarjeta'}
            </h5>
            <p className="text-muted mb-4">
              {requiresDownpayment
                ? 'Vas a completar el pago en una pantalla segura.'
                : 'Vas a cargar los datos de tu tarjeta en una pantalla segura.'}
            </p>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={openPaymentWidget}
              disabled={isWidgetLoading || isPaymentWidgetOpen || isSubmitting || paymentStatus === null}
            >
              {(isWidgetLoading || isPaymentWidgetOpen || isSubmitting) && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              )}
              {isSubmitting
                ? 'Validando...'
                : isPaymentWidgetOpen
                  ? 'Completá el pago'
                  : requiresDownpayment
                    ? 'Ir a pagar'
                    : 'Cargar tarjeta'}
            </button>
            {isPaymentWidgetOpen && (
              <p className="text-muted small mt-3 mb-0">
                Si cerrás el formulario, podés volver a abrirlo desde este botón.
              </p>
            )}
          </div>
        )}

        {renderErrorAlert()}
      </div>

      {/* Status message */}
      {isCardSaved && (
        <div className="text-center mt-4">
          <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-success mb-3" />
          <h3 className="text-success">{requiresDownpayment ? '¡Pago registrado!' : '¡Tarjeta registrada!'}</h3>
          <p className="lead">
            {requiresDownpayment
              ? 'Registramos tu pago. Estamos avanzando con tu compra...'
              : 'Registramos tu tarjeta. Estamos avanzando con tu compra...'}
          </p>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Procesando...</span>
          </div>
        </div>
      )}

      <InfoModal />
    </div>
  );
};

export default PaymentProcessingStep;
